/**
 * competitors: how a chart point of each widget maps back to the rows behind it,
 * for the conversations drill-down. Same filtering as the widget's loader —
 * every competitors widget reads the chip-filtered category (all brands).
 *
 *  - a brand slice/bar (`category: { dimension: 'brand' }`) → that brand's rows
 *    (the prototype's `rows.filter(brand === slice)` / `pivot.rowsAt`);
 *  - a time bucket (`bucket`) → every brand's rows in that bucket (the
 *    prototype's `aggregate(…, splitBy: 'brand').rowsAt`).
 */
import type { DrillRef } from '@/data-sources/contract/conversations';
import { bucketStart } from '@/lib/analytics/buckets';
import { isoToMs } from '@/lib/analytics/dates';

import type { MentionRow } from '../engine/generator';
import { shareOfVoiceRows } from '../widgets/competitors';
import { scoped } from '../query';
import type { DrillResolver, DrillResolvers } from './types';

function narrow(rows: readonly MentionRow[], ref: DrillRef): readonly MentionRow[] {
  let out = rows;
  const bucket = ref.bucket;
  if (bucket) {
    const start = isoToMs(bucket.start);
    out = out.filter((row) => bucketStart(row.day, bucket.unit) === start);
  }
  const category = ref.category;
  if (category?.dimension === 'brand') out = out.filter((row) => row.brand === category.value);
  if (category?.dimension === 'topic') out = out.filter((row) => row.topic === category.value);
  return out;
}

const categoryResolver: DrillResolver = (ref, query) => narrow(scoped(query).category, ref);

export const competitorsDrillResolvers: DrillResolvers = {
  /*
   * A slice is one brand. The ref cannot carry the widget's `category` param,
   * so the rows are taken with the `Brands` selection lifted (`all`) and then
   * narrowed to the clicked brand — identical in both modes, because a brand
   * drawn in `chip` mode is necessarily a selected one.
   */
  'share-of-voice': (ref, query) => narrow(shareOfVoiceRows(query, 'all').current, ref),
  'competitor-sentiment-comparison': categoryResolver,
  'share-of-positive': categoryResolver,
  'share-of-voice-over-time': categoryResolver,
  'competitor-virality': categoryResolver,
  'mention-volume-vs-competitors': categoryResolver,
  /* A topic of the executive Topic ownership: every brand's rows on it. */
  'topic-ownership': categoryResolver,
};
